import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { Link } from "react-router-dom";
import { SPACING } from "../../../shared/constants/spacing";
import { normalizeCountryName } from "../../../utils/countryMapping";
import { normalizeLanguageNames } from "../../../utils/languageMapping";
import { ActionButtons } from "./ActionButtons";

export interface TitleHeroProps {
  readonly title: ApiTitle;
  readonly onPlay: () => void;
  readonly onPlayTrailer?: () => void;
  readonly hasTrailer: boolean;
  readonly likeState: {
    liked: boolean;
    totalLikes: number;
    toggle: () => Promise<void>;
    mutating: boolean;
  };
  readonly watchlistState: {
    inWatchlist: boolean;
    toggle: () => Promise<void>;
    mutating: boolean;
  };
  readonly actionButtonsClassName?: string;
}

/**
 * Metadata Row Component
 */
function MetadataRow({
  title,
  runtimeMin,
  languageNames,
}: {
  readonly title: ApiTitle;
  readonly runtimeMin: number | null;
  readonly languageNames: string[];
}): ReactElement {
  return (
    <div className="flex items-center pb-1 md:pb-2 gap-1 md:gap-2 text-xs md:text-sm text-neutral-200">
      {title.year && <span>{title.year}</span>}
      {title.year && runtimeMin && <span className="mx-1 md:mx-2">•</span>}
      {runtimeMin && <span>{runtimeMin} min</span>}
      {(title.year || runtimeMin) && title.resolution && <span className="mx-1 md:mx-2">•</span>}
      {title.resolution && (
        <span
          className={`${SPACING.badges.padding} bg-neutral-800 border border-neutral-700 rounded text-[10px] md:text-xs font-semibold tracking-wide`}
        >
          {title.resolution}
        </span>
      )}
      {(title.year || runtimeMin || title.resolution) && languageNames.length > 0 && (
        <span className="mx-1 md:mx-2">•</span>
      )}
      {languageNames.length > 0 && <span>{languageNames.join(", ")}</span>}
    </div>
  );
}

/**
 * Format a slug into a display name (e.g., "the-forge" → "The Forge")
 */
function formatProviderName(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Genres, Country, and Provider Row Component
 */
function GenresCountryRow({ title }: { readonly title: ApiTitle }): ReactElement | null {
  const hasGenres = title.genres && title.genres.length > 0;
  const hasCountry = Boolean(title.country);
  const hasProvider = Boolean(title.providerSlug);

  if (!hasGenres && !hasCountry && !hasProvider) {
    return null;
  }

  return (
    <div className="flex flex-wrap pb-1 sm:pb-2 gap-1 sm:gap-2 items-center">
      {hasGenres &&
        title.genres!.map((genre) => (
          <span key={genre} className="py-0.5 sm:py-1 text-xs md:text-sm text-neutral-200">
            {genre}
          </span>
        ))}
      {hasGenres && hasCountry && <span className="mx-1 sm:mx-2">•</span>}
      {hasCountry && (
        <span className="py-0.5 sm:py-1 font-medium text-xs md:text-sm text-neutral-200">
          {normalizeCountryName(title.country!)}
        </span>
      )}
      {(hasGenres || hasCountry) && hasProvider && <span className="mx-1 sm:mx-2">•</span>}
      {hasProvider && title.providerSlug && (
        <Link
          to={`/home/providers/${title.providerSlug}`}
          viewTransition
          className="py-0.5 sm:py-1 text-xs md:text-sm text-lumi-accent hover:text-lumi-accent-light font-medium transition-colors"
        >
          {formatProviderName(title.providerSlug)}
        </Link>
      )}
    </div>
  );
}

export function TitleHero({
  title,
  onPlay,
  onPlayTrailer,
  hasTrailer,
  likeState,
  watchlistState,
  actionButtonsClassName = "",
}: TitleHeroProps): ReactElement {
  const runtimeMin = title.runtimeMinutes || (title.durationSec ? Math.round(title.durationSec / 60) : null);
  const languageNames = title.languages ? normalizeLanguageNames(title.languages) : [];

  return (
    <section className="relative pointer-events-auto">
      {/* Title Info */}
      <div className="relative z-10 px-4 md:px-8 lg:px-16 pb-6 md:pb-8">
        <div className="max-w-2xl lg:max-w-3xl space-y-0">
          {/* Title - Logo if available, otherwise text */}
          {title.logoUrl ? (
            <img
              src={title.logoUrl}
              alt={title.name}
              className="h-28 md:h-36 lg:h-48 w-auto max-w-full object-contain pb-4 md:pb-8 mr-auto"
            />
          ) : (
            <h1 className="text-5xl md:text-6xl lg:text-8xl pb-4 md:pb-8 font-bold leading-tight">{title.name}</h1>
          )}

          {/* Enhanced metadata row */}
          <MetadataRow title={title} runtimeMin={runtimeMin} languageNames={languageNames} />

          {/* Genres and Country */}
          <GenresCountryRow title={title} />

          {/* Director */}
          {title.directorName && (
            <p className="text-xs md:text-sm text-neutral-200 pb-4 sm:pb-8">
              Directed by <span className="text-white font-medium">{title.directorName}</span>
            </p>
          )}

          {/* Synopsis */}
          <p className="text-sm md:text-base text-neutral-200 pb-4 sm:pb-8 leading-relaxed">
            {title.synopsis || "An incredible story waiting to unfold."}
          </p>

          <div className={actionButtonsClassName}>
            <ActionButtons
              onPlay={onPlay}
              hasTrailer={hasTrailer}
              onPlayTrailer={onPlayTrailer}
              watchlistState={watchlistState}
              likeState={likeState}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
